import { AcneIcon } from '@curology/ui-components-web/react/Icon/icons/AcneIcon';
import { CloggedPoresIcon } from '@curology/ui-components-web/react/Icon/icons/CloggedPoresIcon';
import { DarkSpotsIcon } from '@curology/ui-components-web/react/Icon/icons/DarkSpotsIcon';
import { FirmnessIcon } from '@curology/ui-components-web/react/Icon/icons/FirmnessIcon';
import { RosaceaIcon } from '@curology/ui-components-web/react/Icon/icons/RosaceaIcon';
import { TextureIcon } from '@curology/ui-components-web/react/Icon/icons/TextureIcon';
import { WrinklesIcon } from '@curology/ui-components-web/react/Icon/icons/WrinklesIcon';

type GoalNameType =
  | 'acne'
  | 'cloggedPores'
  | 'darkSpots'
  | 'firmness'
  | 'rednessOrRosacea'
  | 'skinTexture'
  | 'wrinkles';

interface GoalType {
  name: GoalNameType;
  formattedName: string;
  description: string;
  icon: any;
}

type FormulaTargetsType = GoalType[];

const GOALS: { [Key in GoalNameType]: GoalType } = {
  acne: {
    name: 'acne',
    formattedName: 'Acne',
    description: 'Clear acne',
    icon: AcneIcon,
  },
  cloggedPores: {
    name: 'cloggedPores',
    formattedName: 'Clogged pores',
    description: 'Treat clogged pores',
    icon: CloggedPoresIcon,
  },
  darkSpots: {
    name: 'darkSpots',
    formattedName: 'Dark spots',
    description: 'Fade dark spots',
    icon: DarkSpotsIcon,
  },
  firmness: {
    name: 'firmness',
    formattedName: 'Firmness',
    description: 'Boost firmness',
    icon: FirmnessIcon,
  },
  rednessOrRosacea: {
    name: 'rednessOrRosacea',
    formattedName: 'Redness or rosacea',
    description: 'Reduce Redness or Rosacea',
    icon: RosaceaIcon,
  },
  skinTexture: {
    name: 'skinTexture',
    formattedName: 'Skin texture',
    description: 'Improve skin texture',
    icon: TextureIcon,
  },
  wrinkles: {
    name: 'wrinkles',
    formattedName: 'Wrinkles',
    description: 'Fight wrinkles',
    icon: WrinklesIcon,
  },
};

interface IngredientTargetMapType {
  [key: string]: {
    targets: FormulaTargetsType;
  };
}

const IngredientTargetMap: IngredientTargetMapType = {
  'Ascorbic Acid': {
    targets: [GOALS.darkSpots, GOALS.wrinkles, GOALS.firmness],
  },
  'Azelaic Acid': {
    targets: [
      GOALS.acne,
      GOALS.cloggedPores,
      GOALS.darkSpots,
      GOALS.skinTexture,
    ],
  },
  Clindamycin: {
    targets: [GOALS.acne],
  },
  Dexpanthenol: {
    targets: [GOALS.skinTexture],
  },
  'Green Tea Extract': {
    targets: [GOALS.darkSpots, GOALS.wrinkles, GOALS.skinTexture],
  },
  Hydrocortisone: {
    targets: [GOALS.darkSpots],
  },
  Hydroquinone: {
    targets: [GOALS.darkSpots],
  },
  Ivermectin: {
    targets: [GOALS.rednessOrRosacea],
  },
  'Kojic Acid': {
    targets: [GOALS.darkSpots],
  },
  Niacinamide: {
    targets: [
      GOALS.acne,
      GOALS.darkSpots,
      GOALS.firmness,
      GOALS.skinTexture,
      GOALS.wrinkles,
    ],
  },
  Resveratrol: {
    targets: [GOALS.darkSpots, GOALS.wrinkles],
  },
  'Tranexamic Acid': {
    targets: [GOALS.darkSpots],
  },
  Tretinoin: {
    targets: [
      GOALS.acne,
      GOALS.darkSpots,
      GOALS.firmness,
      GOALS.cloggedPores,
      GOALS.skinTexture,
      GOALS.wrinkles,
    ],
  },
  'Vitamin C': {
    targets: [GOALS.firmness, GOALS.wrinkles],
  },
  'Zinc Pyrithione': {
    targets: [GOALS.acne],
  },
};

export const getFormulaTargets = (
  ingredients: string[],
): FormulaTargetsType => {
  const targets = ingredients.reduce((result, ingredient) => {
    const config = IngredientTargetMap[ingredient];

    if (!config || !config.targets) {
      return result;
    }

    return result.concat(config.targets);
  }, [] as FormulaTargetsType);

  return Array.from(new Set(targets));
};

export const PRODUCT_IDS = {
  customFormula: 1,
  futureProof: 16,
  deepCleanse: 17,
  bodyCleanse: 31,
  dayPrime: 37,
};

const ProductImagesMap: {
  [key: number]: string;
} = {
  [PRODUCT_IDS.customFormula]:
    'https://images.ctfassets.net/8pzh4g8cs30t/6prM4LU3P0Nh4isoUqFrQj/537dbe5739f377221122727d0af194f1/CF_Full_2x.png',
  [PRODUCT_IDS.futureProof]:
    'https://images.ctfassets.net/8pzh4g8cs30t/7IwhVIOXzCAo0T5eb01lmO/7bfcaaf4c140b2613ae86ba370433d5f/FP_Full_2x.png',
  [PRODUCT_IDS.deepCleanse]:
    'https://images.ctfassets.net/8pzh4g8cs30t/3myQWHijmnCYPA65bEs6Se/289c6cfa6f58288fb68ae21bc9dbb9b8/DC_Full_2x.png',
  [PRODUCT_IDS.bodyCleanse]:
    'https://images.ctfassets.net/8pzh4g8cs30t/2w0uInBjng08vYcLOLDFBU/4832034a6e1dd26c1a87bf61e1805a71/Body_Rx_Cleanser_2x.jpg',
  [PRODUCT_IDS.dayPrime]:
    'https://images.ctfassets.net/mdcr7mahi0vp/6VWdZrSHQyQZTXPRGcOv9y/e43bf6423e6cc9b191cf203f4b096b08/dayPrime.png',
};

export const getProductImages = (productId: number): string | null => {
  return ProductImagesMap[productId] ?? null;
};
